import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';

import { WorldMapChart } from './WorldMapChart';

interface GeoLocationChartProps {
  countries: Record<string, number>;
  regions: Record<string, number>;
  cities: Record<string, number>;
  isLoading?: boolean;
}

const COLORS = ['#3B82F6', '#8B5CF6', '#10B981', '#F59E0B', '#EF4444', '#6366F1', '#EC4899', '#14B8A6'];

export function GeoLocationChart({ countries, regions, cities, isLoading }: GeoLocationChartProps) {
  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="h-4 bg-gray-200 rounded animate-pulse mb-6"></div>
        <div className="h-64 bg-gray-100 rounded animate-pulse"></div>
      </div>
    );
  }

  // Process data for charts
  const countryData = Object.entries(countries)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 8)
    .map(([name, votes]) => ({ name: name || 'Unknown', votes }));

  const regionData = Object.entries(regions)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 6)
    .map(([name, votes]) => ({ name: name || 'Unknown', votes }));

  const cityData = Object.entries(cities)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 8)
    .map(([name, votes]) => ({ name: name || 'Unknown', votes }));

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3">
          <p className="font-semibold text-gray-900">{label}</p>
          <p className="text-blue-600">
            <span className="inline-block w-3 h-3 bg-blue-500 rounded-full mr-2"></span>
            Votes: {payload[0].value.toLocaleString()}
          </p>
        </div>
      );
    }
    return null;
  };

  const ChartCard = ({ title, data, height = 200 }: { title: string; data: any[]; height?: number }) => (
    <div className="bg-gray-50 rounded-lg p-4">
      <h4 className="text-sm font-semibold text-gray-700 mb-3">{title}</h4>
      <ResponsiveContainer width="100%" height={height}>
        <BarChart data={data} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
          <XAxis 
            dataKey="name" 
            tick={{ fontSize: 12, fill: '#6B7280' }}
            angle={-45}
            textAnchor="end"
            height={60}
          />
          <YAxis tick={{ fontSize: 12, fill: '#6B7280' }} />
          <Tooltip content={<CustomTooltip />} />
          <Bar dataKey="votes" radius={[4, 4, 0, 0]}>
            {data.map((_, index) => (
              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Geographic Distribution</h3>
          <p className="text-sm text-gray-600">Where your feedback is coming from</p>
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold text-blue-600">
            🌍
          </div>
        </div>
      </div>

      {/* World Map - Full Width */}
      <div className="mb-6">
        <WorldMapChart countries={countries} isLoading={isLoading} />
      </div>

      {/* Regional Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        <ChartCard title="Top Countries" data={countryData} height={250} />
        <ChartCard title="Top Regions" data={regionData} height={250} />
        <ChartCard title="Top Cities" data={cityData} height={250} />
      </div>

      {countryData.length === 0 && regionData.length === 0 && cityData.length === 0 && (
        <div className="text-center py-12">
          <div className="text-4xl mb-4">🌍</div>
          <p className="text-gray-500">No geographic data available yet</p>
          <p className="text-sm text-gray-400">Data will appear as feedback is received</p>
        </div>
      )}
    </div>
  );
} 